/** Stock, stock history and adjustments (docs/specs/inventory.md). */
export const inventory = {
    stock: {
        title: "Stock",
        description: "What is on hand at each branch and warehouse. Every change is kept in the stock history.",
        columns: {
            product: "Product",
            sku: "SKU",
            location: "Location",
            onHand: "On hand",
            reserved: "Reserved",
            available: "Available",
            avgCost: "Average cost",
            inTransit: "In transit",
        },
        filters: { location: "Location", state: "Stock", all: "All", search: "Find a product" },
        states: { negative: "Below zero", zero: "Out of stock", positive: "In stock" },
        empty: "No stock yet. Enter opening stock or receive goods to start.",
        emptySearch: "No stock for products matching “{{q}}”.",
        adjust: "Adjust stock",
        opening: "Opening stock",
    },
    variant: {
        title: "Stock of {{name}}",
        total: "Total",
        inTransit: "On the way here: {{qty}}",
        noStock: "No stock at your locations yet.",
        history: "History",
        adjust: "Adjust",
    },
    history: {
        title: "Stock history",
        description: "{{name}} at {{location}}",
        from: "From",
        to: "To",
        apply: "Show",
        clear: "Clear",
        openingBalance: "Balance before",
        columns: {
            when: "When",
            type: "Type",
            change: "Change",
            balance: "Balance",
            cost: "Unit cost",
            note: "Note",
        },
        empty: "No stock changes in this period.",
        pickVariant: "Open a product's stock to see its history.",
    },
    types: {
        opening: "Opening stock",
        purchase_receipt: "Goods received",
        purchase_return: "Returned to supplier",
        sale: "Sale",
        sale_return: "Customer return",
        transfer_out: "Transfer out",
        transfer_in: "Transfer in",
        adjustment: "Adjustment",
    },
    reasons: {
        damage: "Damaged",
        loss: "Lost",
        theft: "Stolen",
        expiry: "Expired",
        count_correction: "Count correction",
        found: "Found",
        other: "Other",
        opening: "Opening stock",
    },
    reasonHints: {
        decrease: "Damaged, lost, stolen and expired only reduce stock; found only adds.",
    },
    adjustments: {
        title: "Stock adjustments",
        description: "Manual changes with a reason, and opening stock. Each one is in the audit log.",
        new: "New adjustment",
        newOpening: "Enter opening stock",
        columns: { date: "Date", location: "Location", reason: "Reason", lines: "Lines", note: "Note" },
        filters: { location: "Location", reason: "Reason", all: "All" },
        empty: "No adjustments yet.",
        back: "All adjustments",
        detailTitle: "Adjustment",
        lines: "Lines",
    },
    form: {
        titleAdjust: "Adjust stock",
        titleOpening: "Enter opening stock",
        descriptionAdjust: "Add or remove stock with a reason. Use a minus sign to remove (e.g. -2).",
        descriptionOpening: "The first stock of products at a location. Later changes are adjustments.",
        location: "Location",
        reason: "Reason",
        note: "Note",
        noteRequired: "Say why in the note.",
        addProduct: "Add a product",
        searchPlaceholder: "Name, SKU or barcode",
        searchUnavailable: "Product search is not available right now.",
        noResults: "No products found.",
        lines: "Products",
        noLines: "Add at least one product.",
        quantity: "Quantity",
        quantityAdjust: "Change (+/−)",
        unitCost: "Unit cost",
        unitCostHint: "Optional. Default: the current average cost (opening stock: the product's cost).",
        current: "Now: {{qty}}",
        after: "After: {{qty}}",
        remove: "Remove",
        invalidQuantity: "Enter a number with at most {{decimals}} decimals.",
        zeroQuantity: "The change can't be zero.",
        submit: "Save adjustment",
        submitOpening: "Save opening stock",
        saved: "Stock adjusted.",
        savedOpening: "Opening stock saved.",
    },
    transfers: {
        title: "Transfers",
        description: "Stock moving between your branches and warehouses: asked, approved, sent and received.",
        new: "New transfer",
        columns: { number: "Number", route: "From → to", status: "Status", lines: "Lines", date: "Created" },
        filters: {
            status: "Status",
            location: "Location",
            direction: "Direction",
            all: "All",
            in: "Coming in",
            out: "Going out",
        },
        statuses: {
            draft: "Draft",
            requested: "Waiting for approval",
            approved: "Approved",
            dispatched: "In transit",
            received: "Received (check differences)",
            closed: "Closed",
            cancelled: "Cancelled",
        },
        empty: "No transfers yet.",
        back: "All transfers",
        route: "{{from}} → {{to}}",
        steps: {
            requested: "Requested",
            approved: "Approved",
            dispatched: "Sent",
            received: "Received",
            closed: "Closed",
            cancelled: "Cancelled",
        },
        lines: {
            title: "Products",
            product: "Product",
            requested: "Asked",
            sent: "Sent",
            received: "Received",
            variance: "Difference",
        },
        actions: {
            request: "Ask for approval",
            approve: "Approve",
            dispatch: "Send",
            receive: "Receive",
            close: "Accept differences and close",
            cancel: "Cancel transfer",
            pdf: "Download PDF",
        },
        done: {
            created: "Transfer created.",
            request: "Sent for approval.",
            approve: "Transfer approved.",
            dispatch: "Goods sent. They are in transit until received.",
            receive: "Goods received.",
            close: "Transfer closed.",
            cancel: "Transfer cancelled.",
        },
        cancelTitle: "Cancel this transfer?",
        cancelBody: "Nothing has been sent yet; the transfer stays in the list as cancelled.",
        conflict: "Someone else changed this transfer. The latest version is shown; try again.",
        form: {
            title: "New transfer",
            description: "Pick where the stock comes from and where it goes, then the products.",
            from: "From",
            to: "To",
            note: "Note",
            available: "Available there: {{qty}}",
            submit: "Create draft",
            sameLocation: "From and to must be different locations.",
        },
        dispatch: {
            title: "Send",
            description: "Change a quantity if less goes out than was asked. Reserved stock cannot be sent.",
            submit: "Send now",
        },
        receive: {
            title: "Receive",
            description: "Scan each item, or type the quantities. Explain any difference from what was sent.",
            scan: "Scan barcode or SKU",
            scanHint: "Press Enter after typing a code; scanners do this by themselves.",
            notFound: "“{{code}}” is not on this transfer.",
            added: "+1 {{name}}",
            reason: "Why different?",
            varianceNote: "Note",
            submit: "Book received goods",
        },
        variances: {
            short_shipped: "Short shipped",
            damaged_in_transit: "Damaged in transit",
            lost_in_transit: "Lost in transit",
            extra_received: "Extra received",
            other: "Other",
        },
    },
    counts: {
        title: "Stock counts",
        description: "Count what is on the shelf. Sales can go on while you count; each product is compared with its stock at the moment it was counted.",
        new: "New count",
        columns: {
            number: "Number",
            location: "Location",
            scope: "What",
            status: "Status",
            progress: "Counted",
            date: "Started",
        },
        filters: { status: "Status", location: "Location", all: "All" },
        statuses: {
            counting: "Counting",
            submitted: "Waiting for approval",
            approved: "Approved",
            cancelled: "Cancelled",
        },
        scopes: { full: "Everything", partial: "Some categories or brands" },
        empty: "No stock counts yet.",
        back: "All stock counts",
        progress: "{{counted}} of {{lines}}",
        steps: { started: "Started", submitted: "Submitted", approved: "Approved", cancelled: "Cancelled" },
        blindNote: "Blind count: expected quantities are hidden from counters.",
        form: {
            title: "New stock count",
            description: "The stock on hand is noted when you start. Products sold while you count are taken into account.",
            location: "Location",
            scope: "What to count",
            categories: "Categories (with their subcategories)",
            brands: "Brands",
            blind: "Hide expected quantities from counters",
            note: "Note",
            submit: "Start counting",
            noFilter: "Choose at least one category or brand.",
        },
        counting: {
            title: "Count",
            scan: "Scan barcode or SKU",
            scanHint: "Each scan adds one. Press Enter after typing a code; scanners do this by themselves.",
            notFound: "“{{code}}” is not in this count. Add it with the search below.",
            added: "+1 {{name}}",
            counted: "Counted",
            expected: "Expected at start",
            notCounted: "Not counted",
            save: "Save",
            saved: "Saved.",
            show: "Show",
            showAll: "All",
            showUncounted: "Not counted",
            showCounted: "Counted",
            noLines: "No products here.",
        },
        actions: {
            submit: "Hand in for approval",
            reopen: "Back to counting",
            cancel: "Cancel count",
            approve: "Approve and adjust stock",
        },
        done: {
            created: "Counting started.",
            submit: "Count handed in.",
            reopen: "Back to counting.",
            cancel: "Count cancelled.",
            approve: "Count approved. Stock adjusted.",
        },
        cancelTitle: "Cancel this count?",
        cancelBody: "Nothing is posted; the count stays in the list as cancelled.",
        conflict: "Someone else changed this count. The latest version is shown; try again.",
        variance: {
            title: "Differences",
            description: "Expected = stock at the moment each product was counted (start + sales and other changes until then).",
            product: "Product",
            snapshot: "At start",
            movements: "Changes until counted",
            expected: "Expected",
            counted: "Counted",
            difference: "Difference",
            value: "Value",
            late: "Late sync",
            lateHint: "Sales from before counting that arrived after approval. Check and adjust by hand if needed.",
            totals: "{{withVariance}} of {{lines}} products differ · gain {{gain}} · loss {{loss}}",
            uncounted: "Products not counted",
            uncountedZero: "Set them to zero (not on the shelf)",
            uncountedSkip: "Leave their stock as it is",
            onlyDifferences: "Only differences",
        },
    },
    settings: {
        title: "Stock defaults",
        description: "Used for new products. Each product can change its own setting.",
        trackInventory: "Track stock for new products",
        allowNegative: "Allow selling below zero for new products",
        allowNegativeHint: "The POS always records offline sales; below-zero stock is flagged for you.",
        saved: "Stock defaults saved.",
        conflict: "Someone else changed these settings. The latest values are shown; make your change again.",
    },
};
//# sourceMappingURL=inventory.js.map